import React from 'react';
import { osc, wobble, lerp, loop, clamp, easeOut, easeInOut } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES (Anivaze v2) — Nature & Life edition.
 * The concept HAPPENS in front of you instead of being labelled in a box.
 * Kindergarten-friendly, pure function of `t` (deterministic + exportable).
 * See design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ━━━ Seasons: one friendly tree cycles Spring → Summer → Autumn → Winter ━━━ */
export interface SeasonsSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since scene started. Pure function; default 0. */
  t?: number;
}

export const SeasonsScene: React.FC<SeasonsSceneProps> = ({
  opacity = 1,
  accentColor = '#22c55e',
  t = 0,
  label = 'The four seasons go round each year!',
}) => {
  const CYCLE = 12;        // seconds for a full four-season loop
  const phase = loop(t, CYCLE);   // 0 → 1 over 12 s

  // Season index (0=Spring,1=Summer,2=Autumn,3=Winter) and sub-phase within it
  const seasonRaw = phase * 4;
  const season    = Math.floor(seasonRaw) % 4;
  const sub       = seasonRaw - Math.floor(seasonRaw); // 0→1 within this season

  // Cross-fade: soft fade-out in last 15% of each season
  const fadeIn  = clamp(sub / 0.15, 0, 1);
  const fadeOut = sub > 0.85 ? clamp(1 - (sub - 0.85) / 0.15, 0, 1) : 1;
  const seasonOpacity = easeInOut(fadeIn) * easeInOut(fadeOut);

  // ── Sky gradients per season ──────────────────────────────────────────────
  const skyColors: Array<[string, string]> = [
    ['#bae6fd', '#e0f2fe'],   // Spring: light blue
    ['#7dd3fc', '#fde68a'],   // Summer: bright blue → warm
    ['#fbbf24', '#fed7aa'],   // Autumn: amber
    ['#93c5fd', '#e2e8f0'],   // Winter: pale blue-grey
  ];
  const groundColors = ['#86efac', '#4ade80', '#d97706', '#e2e8f0'];

  // Blend between current and next season sky
  const nextSeason  = (season + 1) % 4;
  const blendAmt    = sub;

  function blendHex(a: string, b: string, f: number): string {
    const p = (s: string) => [
      parseInt(s.slice(1, 3), 16),
      parseInt(s.slice(3, 5), 16),
      parseInt(s.slice(5, 7), 16),
    ];
    const ca = p(a), cb = p(b);
    const r = Math.round(ca[0] + (cb[0] - ca[0]) * f);
    const g = Math.round(ca[1] + (cb[1] - ca[1]) * f);
    const bv = Math.round(ca[2] + (cb[2] - ca[2]) * f);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bv.toString(16).padStart(2, '0')}`;
  }

  const skyTop    = blendHex(skyColors[season][0], skyColors[nextSeason][0], blendAmt);
  const skyBot    = blendHex(skyColors[season][1], skyColors[nextSeason][1], blendAmt);
  const groundCol = blendHex(groundColors[season], groundColors[nextSeason], blendAmt);

  const treeX = 400, treeY = 310;

  // ── Sun properties ────────────────────────────────────────────────────────
  const sunX       = 128;
  const sunY       = season === 1 ? 70 : 90;   // Summer sun sits higher
  const sunRadius  = season === 1 ? 36 : 28;
  const sunRayAng  = (t * 10) % 360;
  const sunVisible = season !== 3;              // No sun in Winter

  // ── Spring: blossoms ──────────────────────────────────────────────────────
  const blossomCount = 18;
  const blossoms = Array.from({ length: blossomCount }, (_, k) => {
    const angle = (k / blossomCount) * Math.PI * 2;
    const dist  = 28 + (k % 3) * 14;
    const bx    = treeX + dist * Math.cos(angle) + wobble(t, 1.6 + k * 0.11, 4, k);
    const by    = treeY - 60 + dist * Math.sin(angle) * 0.6 + wobble(t, 1.3 + k * 0.07, 3, k + 1);
    return { bx, by };
  });

  // ── Summer: full green canopy ─────────────────────────────────────────────
  const canopyPulse = osc(t, 2.4, 0.97, 1.03);

  // ── Autumn: falling leaves ────────────────────────────────────────────────
  const leafCount = 14;
  const leaves = Array.from({ length: leafCount }, (_, k) => {
    const speed   = 0.05 + (k % 5) * 0.018;
    const phase2  = (loop(t * speed + k * 0.13, 1));
    const lx      = treeX - 80 + (k * 31) % 160 + wobble(t, 1.5 + k * 0.09, 12, k);
    const ly      = lerp(treeY - 80, treeY + 40, easeInOut(phase2));
    const lrot    = phase2 * 360 * (k % 2 === 0 ? 1 : -1);
    const leafOpacity = clamp(1 - phase2 * 1.2, 0, 1);
    const leafColor = k % 3 === 0 ? '#ef4444' : k % 3 === 1 ? '#f97316' : '#fbbf24';
    return { lx, ly, lrot, leafOpacity, leafColor };
  });

  // ── Winter: snowflakes ────────────────────────────────────────────────────
  const snowCount = 20;
  const snowflakes = Array.from({ length: snowCount }, (_, k) => {
    const speed  = 0.03 + (k % 4) * 0.012;
    const fall   = loop(t * speed + k * 0.17, 1);
    const sx     = 80 + (k * 37) % 640 + wobble(t, 2.2 + k * 0.06, 10, k);
    const sy     = lerp(-10, 380, fall);
    const sr     = 2.5 + (k % 3) * 1.5;
    const sOpacity = clamp(0.6 + 0.4 * osc(t, 1.2, 0, 1, k), 0, 1);
    return { sx, sy, sr, sOpacity };
  });

  // Snowman (Winter only) — sits at base of tree
  const snowmanX = treeX + 110, snowmanY = treeY + 30;
  const snowmanBlink = osc(t, 3.4, 0, 1) > 0.92 ? 0.1 : 1; // rare blink
  const snowmanArmWave = wobble(t, 1.8, 6);

  // Season label
  const seasonNames = ['Spring', 'Summer', 'Autumn', 'Winter'];
  const seasonColors = ['#ec4899', '#f59e0b', '#ea580c', '#64748b'];

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="sns-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={skyTop} />
          <stop offset="100%" stopColor={skyBot} />
        </linearGradient>
        <radialGradient id="sns-sun" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fffde7" />
          <stop offset="65%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
      </defs>

      {/* Sky + ground */}
      <rect x={0} y={0} width={800} height={450} fill="url(#sns-sky)" />
      <rect x={0} y={treeY + 30} width={800} height={450 - (treeY + 30)} fill={groundCol} />

      {/* ── Sun (Spring + Summer + Autumn) ── */}
      {sunVisible && (
        <g transform={`translate(${sunX} ${sunY})`}>
          <g transform={`rotate(${sunRayAng})`} opacity={0.9}>
            {Array.from({ length: 10 }, (_, k) => {
              const a = (k / 10) * Math.PI * 2;
              return (
                <line
                  key={k}
                  x1={(sunRadius + 5) * Math.cos(a)}
                  y1={(sunRadius + 5) * Math.sin(a)}
                  x2={(sunRadius + 18) * Math.cos(a)}
                  y2={(sunRadius + 18) * Math.sin(a)}
                  stroke="#fcd34d"
                  strokeWidth={3}
                  strokeLinecap="round"
                />
              );
            })}
          </g>
          <circle r={sunRadius} fill="url(#sns-sun)" />
          <circle cx={-8} cy={-7} r={4} fill="rgba(0,0,0,0.12)" />
          <circle cx={5} cy={-9} r={3} fill="rgba(0,0,0,0.12)" />
          <path d="M -8 5 Q 0 11 8 5" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth={2} strokeLinecap="round" />
        </g>
      )}

      {/* ── Winter clouds / snow sky ── */}
      {season === 3 && (
        <>
          {[{ x: 140, y: 80, s: 1.1 }, { x: 360, y: 55, s: 0.85 }, { x: 580, y: 70, s: 1 }].map((c, i) => {
            const cx = ((c.x - (t * 9) % 800) + 800) % 800;
            return (
              <g key={i} transform={`translate(${cx} ${c.y}) scale(${c.s})`} opacity={0.75}>
                <ellipse cx={0}   cy={0}  rx={38} ry={22} fill="#e2e8f0" />
                <ellipse cx={38}  cy={6}  rx={30} ry={18} fill="#f1f5f9" />
                <ellipse cx={-32} cy={8}  rx={26} ry={16} fill="#f1f5f9" />
              </g>
            );
          })}
          {snowflakes.map((s, k) => (
            <circle key={k} cx={s.sx} cy={s.sy} r={s.sr} fill="#fff" opacity={s.sOpacity} />
          ))}
          {/* Snowman */}
          <g transform={`translate(${snowmanX} ${snowmanY})`} opacity={seasonOpacity}>
            <ellipse cx={0} cy={0} rx={22} ry={7} fill="rgba(0,0,0,0.12)" />
            <circle cx={0} cy={-18} r={22} fill="white" stroke="#cbd5e1" strokeWidth={1.5} />
            <circle cx={0} cy={-52} r={15} fill="white" stroke="#cbd5e1" strokeWidth={1.5} />
            {/* Eyes */}
            <circle cx={-5} cy={-56} r={2.5 * snowmanBlink} fill="#1e293b" />
            <circle cx={5}  cy={-56} r={2.5 * snowmanBlink} fill="#1e293b" />
            {/* Carrot nose */}
            <path d="M 0 -53 L 8 -51 L 0 -49 Z" fill="#f97316" />
            {/* Smile */}
            <path d="M -5 -47 Q 0 -43 5 -47" fill="none" stroke="#1e293b" strokeWidth={1.5} strokeLinecap="round" />
            {/* Scarf */}
            <path d="M -14 -40 Q 0 -36 14 -40" fill="none" stroke="#ef4444" strokeWidth={5} strokeLinecap="round" />
            {/* Arms */}
            <line x1={-22} y1={-20} x2={-38 + snowmanArmWave} y2={-32 - snowmanArmWave * 0.5} stroke="#78716c" strokeWidth={3} strokeLinecap="round" />
            <line x1={22}  y1={-20} x2={40}  y2={-32} stroke="#78716c" strokeWidth={3} strokeLinecap="round" />
            {/* Buttons */}
            <circle cx={0} cy={-22} r={2} fill="#64748b" />
            <circle cx={0} cy={-14} r={2} fill="#64748b" />
          </g>
        </>
      )}

      {/* ── Spring clouds (drifting white puffs) ── */}
      {season === 0 && (
        [{ x: 550, y: 80, s: 1 }, { x: 720, y: 110, s: 0.75 }].map((c, i) => {
          const cx = ((c.x - (t * 12) % 900) + 900) % 900;
          return (
            <g key={i} transform={`translate(${cx} ${c.y}) scale(${c.s})`} opacity={0.9}>
              <ellipse cx={0}  cy={0}  rx={32} ry={20} fill="#fff" />
              <ellipse cx={32} cy={5}  rx={26} ry={16} fill="#fff" />
              <ellipse cx={-28} cy={7} rx={22} ry={14} fill="#fff" />
            </g>
          );
        })
      )}

      {/* ── Tree trunk ── */}
      <rect
        x={treeX - 12}
        y={treeY - 60}
        width={24}
        height={90}
        rx={8}
        fill="#92400e"
        stroke="#78350f"
        strokeWidth={1.5}
      />

      {/* ── Spring: blossoms ── */}
      {season === 0 && (
        <g opacity={seasonOpacity}>
          <circle cx={treeX} cy={treeY - 90} r={72} fill="#fce7f3" />
          <circle cx={treeX - 40} cy={treeY - 68} r={52} fill="#fbcfe8" />
          <circle cx={treeX + 42} cy={treeY - 72} r={50} fill="#f9a8d4" />
          {blossoms.map((b, k) => (
            <circle key={k} cx={b.bx} cy={b.by} r={5 + (k % 3) * 2} fill="#f472b6" opacity={0.85} />
          ))}
          {/* Petal shower */}
          {Array.from({ length: 8 }, (_, k) => {
            const pfall = loop(t * (0.04 + k * 0.007) + k * 0.2, 1);
            const px = treeX - 90 + (k * 29) % 180 + wobble(t, 1.2, 8, k);
            const py = lerp(treeY - 120, treeY + 50, pfall);
            const prot = pfall * 300 * (k % 2 === 0 ? 1 : -1);
            return (
              <ellipse
                key={k}
                cx={px} cy={py}
                rx={6} ry={4}
                fill="#f9a8d4"
                opacity={clamp(1 - pfall * 1.4, 0, 0.9)}
                transform={`rotate(${prot} ${px} ${py})`}
              />
            );
          })}
        </g>
      )}

      {/* ── Summer: dense green canopy ── */}
      {season === 1 && (
        <g opacity={seasonOpacity} transform={`scale(${canopyPulse})`} style={{ transformOrigin: `${treeX}px ${treeY - 80}px` }}>
          <circle cx={treeX}      cy={treeY - 95} r={78} fill="#16a34a" />
          <circle cx={treeX - 44} cy={treeY - 70} r={56} fill="#22c55e" />
          <circle cx={treeX + 46} cy={treeY - 74} r={54} fill="#4ade80" />
          <circle cx={treeX}      cy={treeY - 60} r={44} fill="#22c55e" />
          <circle cx={treeX - 20} cy={treeY - 115} r={36} fill="#86efac" />
        </g>
      )}

      {/* ── Autumn: orange/red sparse canopy + falling leaves ── */}
      {season === 2 && (
        <g opacity={seasonOpacity}>
          <circle cx={treeX}      cy={treeY - 90} r={65} fill="#ea580c" opacity={0.9} />
          <circle cx={treeX - 38} cy={treeY - 68} r={46} fill="#f97316" opacity={0.85} />
          <circle cx={treeX + 40} cy={treeY - 70} r={44} fill="#ef4444" opacity={0.85} />
          {leaves.map((l, k) => (
            <rect
              key={k}
              x={l.lx - 7}
              y={l.ly - 5}
              width={14}
              height={10}
              rx={4}
              fill={l.leafColor}
              opacity={l.leafOpacity}
              transform={`rotate(${l.lrot} ${l.lx} ${l.ly})`}
            />
          ))}
        </g>
      )}

      {/* ── Winter: bare branches ── */}
      {season === 3 && (
        <g opacity={seasonOpacity}>
          {/* Main branches */}
          <line x1={treeX} y1={treeY - 60} x2={treeX - 55} y2={treeY - 120} stroke="#78350f" strokeWidth={6} strokeLinecap="round" />
          <line x1={treeX} y1={treeY - 60} x2={treeX + 55} y2={treeY - 115} stroke="#78350f" strokeWidth={6} strokeLinecap="round" />
          <line x1={treeX} y1={treeY - 75} x2={treeX - 10} y2={treeY - 145} stroke="#78350f" strokeWidth={5} strokeLinecap="round" />
          {/* Sub-branches */}
          <line x1={treeX - 55} y1={treeY - 120} x2={treeX - 80} y2={treeY - 148} stroke="#92400e" strokeWidth={3} strokeLinecap="round" />
          <line x1={treeX - 55} y1={treeY - 120} x2={treeX - 35} y2={treeY - 150} stroke="#92400e" strokeWidth={3} strokeLinecap="round" />
          <line x1={treeX + 55} y1={treeY - 115} x2={treeX + 80} y2={treeY - 145} stroke="#92400e" strokeWidth={3} strokeLinecap="round" />
          <line x1={treeX + 55} y1={treeY - 115} x2={treeX + 36} y2={treeY - 148} stroke="#92400e" strokeWidth={3} strokeLinecap="round" />
          <line x1={treeX - 10} y1={treeY - 145} x2={treeX - 28} y2={treeY - 168} stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
          <line x1={treeX - 10} y1={treeY - 145} x2={treeX + 14} y2={treeY - 170} stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
          {/* Snow on branches */}
          <ellipse cx={treeX - 67} cy={treeY - 134} rx={14} ry={5} fill="white" opacity={0.9} />
          <ellipse cx={treeX + 67} cy={treeY - 128} rx={13} ry={5} fill="white" opacity={0.9} />
          <ellipse cx={treeX}      cy={treeY - 152} rx={10} ry={4} fill="white" opacity={0.9} />
          {/* Snow on ground */}
          <ellipse cx={treeX} cy={treeY + 32} rx={120} ry={14} fill="white" opacity={0.8} />
        </g>
      )}

      {/* ── Season label badge ── */}
      <g transform={`translate(${treeX} ${treeY + 70})`} opacity={seasonOpacity}>
        <rect
          x={-48} y={-18}
          width={96} height={32}
          rx={14}
          fill={seasonColors[season]}
          opacity={0.92}
        />
        <text
          x={0} y={4}
          textAnchor="middle"
          fill="#fff"
          fontSize={15}
          fontWeight={700}
          fontFamily="Inter,sans-serif"
        >
          {seasonNames[season]}
        </text>
      </g>

      {/* ── Caption ── */}
      <g transform="translate(400 424)">
        <rect x={-270} y={-20} width={540} height={36} rx={18} fill="rgba(255,255,255,0.88)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};

/* ━━━ Butterfly life-cycle: egg → caterpillar → chrysalis → butterfly ━━━━━━━ */
export interface ButterflyLifeSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds since scene started. Pure function; default 0. */
  t?: number;
}

export const ButterflyLifeScene: React.FC<ButterflyLifeSceneProps> = ({
  opacity = 1,
  accentColor = '#a855f7',
  t = 0,
  label = 'From egg to butterfly!',
}) => {
  const CYCLE = 10;       // seconds for a full lifecycle loop
  const phase = loop(t, CYCLE);  // 0 → 1

  // 4 stages; each lasts 25% of the cycle — tiny overlap for cross-fade
  // Stage 0: egg       0.00 – 0.25
  // Stage 1: caterpillar 0.25 – 0.50
  // Stage 2: chrysalis 0.50 – 0.75
  // Stage 3: butterfly 0.75 – 1.00
  const FADE = 0.06; // cross-fade window as fraction of cycle

  function stageOpacity(start: number, end: number): number {
    if (phase < start - FADE) return 0;
    if (phase < start)        return easeOut((phase - (start - FADE)) / FADE);
    if (phase < end - FADE)   return 1;
    if (phase < end)          return easeOut(1 - (phase - (end - FADE)) / FADE);
    return 0;
  }

  const eggOp    = stageOpacity(0,    0.25);
  const catOp    = stageOpacity(0.25, 0.50);
  const chrOp    = stageOpacity(0.50, 0.75);
  const flyOp    = stageOpacity(0.75, 1.0 + FADE); // extends past 1 so butterfly fades into egg

  // ── Shared background ─────────────────────────────────────────────────────
  // Gentle sky: light blue-green meadow feel
  const bgTop = '#d1fae5';
  const bgBot = '#ecfdf5';

  // Big leaf (shared staging prop)
  const leafX = 400, leafY = 280;

  // ── Stage 0: Egg ──────────────────────────────────────────────────────────
  // Small round egg on a leaf, gently pulsing
  const eggPulse = osc(t, 1.8, 0.97, 1.03);
  const eggCrackPhase = clamp((phase - 0.18) / 0.07, 0, 1); // crack appears before transition

  // ── Stage 1: Caterpillar ──────────────────────────────────────────────────
  // Segment count 7; crawls across the leaf
  const catProgress = clamp((phase - 0.25) / 0.22, 0, 1); // 0→1 across the stage
  const catX = lerp(leafX - 100, leafX + 80, easeInOut(catProgress));
  const catY = leafY - 15;
  const catSegments = 7;
  const segR = 13;

  // ── Stage 2: Chrysalis ────────────────────────────────────────────────────
  const chrShimmer = osc(t, 1.2, 0.6, 1);
  const chrPulse   = osc(t, 2.0, 0.96, 1.04);
  const chrSparkleAngle = orbitAngle(t, 2.6);

  // ── Stage 3: Butterfly flying ─────────────────────────────────────────────
  // Flap wings: osc between open (0) and closed (90 deg)
  const wingFlap  = osc(t, 0.35, 5, 70);  // degrees
  const flyPhase  = clamp((phase - 0.75) / 0.22, 0, 1);
  // Fly path: starts on leaf, arcs up and across
  const flyX = lerp(leafX, leafX + 200, easeOut(flyPhase)) + wobble(t, 1.1, 12);
  const flyY = lerp(leafY - 30, 110, easeOut(flyPhase)) + wobble(t, 0.9, 10, 1);
  const bodyTilt = wobble(t, 1.4, 8);

  function orbitAngle(tArg: number, periodSec: number, ph = 0): number {
    return ph + (tArg / periodSec) * Math.PI * 2;
  }

  // Wing shape helper: top-wing bezier (mirrored L/R)
  const WingPair = (cx: number, cy: number, flapDeg: number, wingColor: string) => {
    const flapRad = (flapDeg * Math.PI) / 180;
    const sweep   = Math.cos(flapRad);
    // Upper wings
    const lx1 = cx - 12 * sweep;
    const ly1 = cy - 38;
    const lx2 = cx - 68 * sweep;
    const ly2 = cy - 18;
    const rx1 = cx + 12 * sweep;
    const ry1 = cy - 38;
    const rx2 = cx + 68 * sweep;
    const ry2 = cy - 18;
    // Lower wings (smaller)
    const llx = cx - 8 * sweep;
    const lly = cy + 20;
    const llx2 = cx - 46 * sweep;
    const lly2 = cy + 40;
    const rlx = cx + 8 * sweep;
    const rly = cy + 20;
    const rlx2 = cx + 46 * sweep;
    const rly2 = cy + 40;
    return (
      <g>
        {/* Left upper wing */}
        <path
          d={`M${cx} ${cy} C ${lx1} ${ly1} ${lx2} ${ly2} ${cx - 40 * sweep} ${cy + 5} Z`}
          fill={wingColor}
          opacity={0.88}
          stroke={accentColor}
          strokeWidth={1.5}
        />
        {/* Right upper wing */}
        <path
          d={`M${cx} ${cy} C ${rx1} ${ry1} ${rx2} ${ry2} ${cx + 40 * sweep} ${cy + 5} Z`}
          fill={wingColor}
          opacity={0.88}
          stroke={accentColor}
          strokeWidth={1.5}
        />
        {/* Left lower wing */}
        <path
          d={`M${cx} ${cy + 8} C ${llx} ${lly} ${llx2} ${lly2} ${cx - 24 * sweep} ${cy + 10} Z`}
          fill={wingColor}
          opacity={0.78}
        />
        {/* Right lower wing */}
        <path
          d={`M${cx} ${cy + 8} C ${rlx} ${rly} ${rlx2} ${rly2} ${cx + 24 * sweep} ${cy + 10} Z`}
          fill={wingColor}
          opacity={0.78}
        />
        {/* Wing spots */}
        <circle cx={cx - 30 * sweep} cy={cy - 10} r={4} fill="rgba(255,255,255,0.6)" />
        <circle cx={cx + 30 * sweep} cy={cy - 10} r={4} fill="rgba(255,255,255,0.6)" />
      </g>
    );
  };

  // Caterpillar body
  const Caterpillar = (cx: number, cy: number) => (
    <g>
      {/* Shadow */}
      <ellipse cx={cx} cy={cy + segR + 3} rx={segR * catSegments * 0.55} ry={6} fill="rgba(0,0,0,0.12)" />
      {/* Segments (drawn right-to-left so head is on top) */}
      {Array.from({ length: catSegments }, (_, k) => {
        const sx  = cx + (k - catSegments / 2) * (segR * 1.7);
        const sy  = cy + wobble(t, 0.4 + k * 0.05, 3, k); // wiggle
        const col = k % 2 === 0 ? '#4ade80' : '#22c55e';
        return <circle key={k} cx={sx} cy={sy} r={segR} fill={col} stroke="#15803d" strokeWidth={1.5} />;
      })}
      {/* Head */}
      <circle cx={cx + (catSegments / 2) * (segR * 1.7)} cy={cy} r={segR + 3} fill="#86efac" stroke="#15803d" strokeWidth={2} />
      <circle cx={cx + (catSegments / 2) * (segR * 1.7) + 5} cy={cy - 5} r={3} fill="#1e293b" />
      {/* Antennae */}
      <line
        x1={cx + (catSegments / 2) * (segR * 1.7) + 2}
        y1={cy - segR - 2}
        x2={cx + (catSegments / 2) * (segR * 1.7) + 8 + wobble(t, 0.7, 4)}
        y2={cy - segR - 14}
        stroke="#15803d" strokeWidth={2} strokeLinecap="round"
      />
      <line
        x1={cx + (catSegments / 2) * (segR * 1.7) + 6}
        y1={cy - segR - 2}
        x2={cx + (catSegments / 2) * (segR * 1.7) + 14 + wobble(t, 0.9, 3, 1)}
        y2={cy - segR - 12}
        stroke="#15803d" strokeWidth={2} strokeLinecap="round"
      />
      {/* Little legs paddling */}
      {Array.from({ length: 5 }, (_, k) => {
        const lx = cx + (k - 2) * (segR * 1.8);
        const legOff = wobble(t, 0.35, 6, k * 0.6);
        return (
          <g key={k}>
            <line x1={lx} y1={cy + segR - 3} x2={lx - 4} y2={cy + segR + 8 + legOff} stroke="#15803d" strokeWidth={3} strokeLinecap="round" />
            <line x1={lx} y1={cy + segR - 3} x2={lx + 4} y2={cy + segR + 8 - legOff} stroke="#15803d" strokeWidth={3} strokeLinecap="round" />
          </g>
        );
      })}
    </g>
  );

  // Stage label data
  const stageLabels = [
    { text: 'Egg', color: '#d97706',  ops: eggOp  },
    { text: 'Caterpillar', color: '#16a34a', ops: catOp  },
    { text: 'Chrysalis', color: '#7c3aed',   ops: chrOp  },
    { text: 'Butterfly!',  color: '#db2777',  ops: flyOp  },
  ];

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="bls-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={bgTop} />
          <stop offset="100%" stopColor={bgBot} />
        </linearGradient>
        <radialGradient id="bls-chr" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="70%" stopColor="#7c3aed" />
          <stop offset="100%" stopColor="#4c1d95" />
        </radialGradient>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="url(#bls-bg)" />

      {/* Meadow floor */}
      <ellipse cx={400} cy={380} rx={400} ry={70} fill="#bbf7d0" />
      <ellipse cx={400} cy={390} rx={400} ry={60} fill="#86efac" />

      {/* Background flowers (static, decorative) */}
      {[80, 160, 600, 680, 730].map((fx, i) => (
        <g key={i} transform={`translate(${fx} 340)`}>
          <line x1={0} y1={0} x2={0} y2={30} stroke="#22c55e" strokeWidth={3} />
          <circle cx={0} cy={0} r={10} fill={['#fbbf24', '#f472b6', '#60a5fa', '#f87171', '#a78bfa'][i % 5]} />
        </g>
      ))}

      {/* Grassy stems swaying */}
      {[120, 200, 500, 650].map((gx, i) => (
        <line
          key={i}
          x1={gx} y1={380}
          x2={gx + wobble(t, 2 + i * 0.3, 8, i)}
          y2={340}
          stroke="#4ade80"
          strokeWidth={3}
          strokeLinecap="round"
        />
      ))}

      {/* ── Central leaf — always visible as stage prop ── */}
      <g transform={`translate(${leafX} ${leafY})`}>
        {/* Leaf body */}
        <path
          d="M 0 0 C -80 -40 -100 20 -60 50 C -30 70 30 70 60 50 C 100 20 80 -40 0 0 Z"
          fill="#4ade80"
          stroke="#16a34a"
          strokeWidth={2}
        />
        {/* Leaf vein */}
        <line x1={0} y1={0} x2={0} y2={50} stroke="#16a34a" strokeWidth={2} opacity={0.6} />
        <line x1={0} y1={15} x2={-30} y2={35} stroke="#16a34a" strokeWidth={1.5} opacity={0.5} />
        <line x1={0} y1={15} x2={30}  y2={35} stroke="#16a34a" strokeWidth={1.5} opacity={0.5} />
        {/* Stem */}
        <path
          d="M 0 50 C 10 70 -10 90 0 110"
          fill="none"
          stroke="#15803d"
          strokeWidth={4}
          strokeLinecap="round"
        />
      </g>

      {/* ━━━ Stage 0: Egg ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <g opacity={eggOp}>
        <g transform={`translate(${leafX} ${leafY - 10}) scale(${eggPulse})`}>
          {/* Shadow */}
          <ellipse cx={0} cy={28} rx={18} ry={5} fill="rgba(0,0,0,0.13)" />
          {/* Egg */}
          <ellipse cx={0} cy={0} rx={16} ry={22} fill="#fef9c3" stroke="#fde047" strokeWidth={2} />
          {/* Crack lines appear near end of egg stage */}
          <path
            d="M -3 -12 L 0 -4 L 4 -8 L 2 2"
            fill="none"
            stroke="#ca8a04"
            strokeWidth={1.5}
            strokeLinecap="round"
            opacity={eggCrackPhase}
          />
          <path
            d="M 5 -8 L 3 0 L 7 4"
            fill="none"
            stroke="#ca8a04"
            strokeWidth={1.5}
            strokeLinecap="round"
            opacity={eggCrackPhase}
          />
        </g>
      </g>

      {/* ━━━ Stage 1: Caterpillar ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <g opacity={catOp}>
        {Caterpillar(catX, catY)}
      </g>

      {/* ━━━ Stage 2: Chrysalis ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <g opacity={chrOp}>
        {/* Hanging thread */}
        <line
          x1={leafX}
          y1={leafY - 35}
          x2={leafX}
          y2={leafY - 70}
          stroke="#78716c"
          strokeWidth={2}
          strokeLinecap="round"
        />
        <g
          transform={`translate(${leafX} ${leafY - 100}) scale(${chrPulse})`}
        >
          {/* Chrysalis body */}
          <ellipse cx={0} cy={0} rx={22} ry={34} fill="url(#bls-chr)" />
          {/* Shimmer overlay */}
          <ellipse cx={-6} cy={-10} rx={8} ry={14} fill="rgba(255,255,255,0.22)" opacity={chrShimmer} />
          {/* Segmentation lines */}
          {[-14, -6, 2, 10, 18].map((oy, k) => (
            <line
              key={k}
              x1={-Math.sqrt(Math.max(0, 22 * 22 - oy * oy)) * 0.85}
              y1={oy}
              x2={Math.sqrt(Math.max(0, 22 * 22 - oy * oy)) * 0.85}
              y2={oy}
              stroke="#4c1d95"
              strokeWidth={1}
              opacity={0.45}
            />
          ))}
          {/* Top hook */}
          <ellipse cx={0} cy={-32} rx={5} ry={4} fill="#6d28d9" />
          {/* Sparkle */}
          <circle
            cx={22 * Math.cos(chrSparkleAngle)}
            cy={34 * Math.sin(chrSparkleAngle) * 0.6}
            r={3}
            fill="#fff"
            opacity={osc(t, 0.9, 0, 0.9)}
          />
        </g>
      </g>

      {/* ━━━ Stage 3: Butterfly flying ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <g opacity={flyOp}>
        <g transform={`translate(${flyX} ${flyY}) rotate(${bodyTilt})`}>
          {WingPair(0, 0, wingFlap, accentColor)}
          {/* Body */}
          <ellipse cx={0} cy={8} rx={5} ry={18} fill="#1e293b" />
          {/* Head */}
          <circle cx={0} cy={-10} r={6} fill="#1e293b" />
          <circle cx={-2} cy={-12} r={1.8} fill="#fff" />
          {/* Antennae */}
          <line x1={-3} y1={-15} x2={-12 + wobble(t, 0.5, 3)} y2={-28} stroke="#1e293b" strokeWidth={1.5} strokeLinecap="round" />
          <circle cx={-12 + wobble(t, 0.5, 3)} cy={-28} r={2.5} fill="#f472b6" />
          <line x1={3}  y1={-15} x2={12 - wobble(t, 0.5, 3, 1)} y2={-28} stroke="#1e293b" strokeWidth={1.5} strokeLinecap="round" />
          <circle cx={12 - wobble(t, 0.5, 3, 1)} cy={-28} r={2.5} fill="#f472b6" />
        </g>
        {/* Sparkle trail behind butterfly */}
        {Array.from({ length: 4 }, (_, k) => {
          const trailX = flyX - (k + 1) * 18 + wobble(t, 0.8, 5, k);
          const trailY = flyY + wobble(t, 0.7, 5, k + 2);
          const tOpacity = clamp(osc(t, 0.5, 0, 1, k * 0.8) * (1 - k * 0.2), 0, 1);
          return <circle key={k} cx={trailX} cy={trailY} r={3 - k * 0.5} fill="#fbbf24" opacity={tOpacity} />;
        })}
      </g>

      {/* ── Stage label (centered, changes each stage) ── */}
      {stageLabels.map((sl, k) => (
        <g key={k} transform="translate(400 50)" opacity={sl.ops}>
          <rect x={-60} y={-18} width={120} height={32} rx={14} fill={sl.color} opacity={0.9} />
          <text
            x={0} y={4}
            textAnchor="middle"
            fill="#fff"
            fontSize={15}
            fontWeight={700}
            fontFamily="Inter,sans-serif"
          >
            {sl.text}
          </text>
        </g>
      ))}

      {/* ── Caption ── */}
      <g transform="translate(400 424)">
        <rect x={-200} y={-20} width={400} height={36} rx={18} fill="rgba(255,255,255,0.88)" stroke="#d1fae5" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};
